import type { EventSummary } from '@finder/contracts';
import { overlapsWeekend, weekendRange } from '../filters/weekend.js';

/**
 * Client-side results view for the leftovers of issue #9.
 *
 * Distance (`radiusMiles`) and the date window (`windowDays`) are applied by
 * the API. Everything here runs on the page the API already returned:
 * - `happeningNow` keeps `timingBand === 'ongoing'`
 * - `confirmedOnly` keeps `foodState === 'confirmed'`
 * - offer + hide-restricted stay as shipped in PR #23
 * - sort `best` keeps server ranking order; `soonest` / `nearest` reorder
 *
 * Pure: never mutates the input array, never re-scores.
 */

/** Client-side offer filter. 'either' shows all; 'food'/'drink' narrow. */
export type OfferFilter = 'either' | 'food' | 'drink';

/** This weekend is not a rolling day count. The other three are. */
export type WhenChoice = 'today' | 'couple' | 'weekend' | 'two_weeks';

/** `best` is the server ranking. The other two are explicit visitor sorts. */
export type SortMode = 'best' | 'soonest' | 'nearest';

export interface ListFilters {
  offer: OfferFilter;
  /** When true, hide events that carry a restricting audience tag (issue #8). */
  hideRestricted: boolean;
  /** When true, keep only events whose food signal is confirmed. */
  confirmedOnly: boolean;
  /** When true, keep only events that are happening now (`timingBand === 'ongoing'`). */
  happeningNow: boolean;
  sort: SortMode;
  /** Echoed in the results announcement. Filtering itself is server-side. */
  radiusMiles?: number;
  /** Echoed in the results announcement. Filtering itself is server-side. */
  windowDays?: number;
  /** Weekend is applied here. The other choices are already the API window. */
  when?: WhenChoice;
  /** Clock for the weekend range. Tests pass a fixed instant. */
  now?: Date;
}

export const DEFAULT_LIST_FILTERS: ListFilters = {
  offer: 'either',
  hideRestricted: false,
  confirmedOnly: false,
  happeningNow: false,
  sort: 'best',
};

/**
 * Drop rows the visitor has filtered out. Server order is preserved — this
 * only removes, it never reorders.
 */
export function applyFilters(
  events: readonly EventSummary[],
  filters: ListFilters,
): EventSummary[] {
  return events.filter((event) => {
    if (filters.offer === 'food' && event.offers.food !== 'yes') return false;
    if (filters.offer === 'drink' && event.offers.drink !== 'yes') return false;
    if (filters.hideRestricted && event.audienceTags.some((tag) => tag.restricted)) return false;
    if (filters.confirmedOnly && event.foodState !== 'confirmed') return false;
    if (filters.happeningNow && event.timingBand !== 'ongoing') return false;
    if (filters.when === 'weekend') {
      const range = weekendRange(filters.now ?? new Date());
      if (!overlapsWeekend(event.startInstant, event.endInstant, range)) return false;
    }
    return true;
  });
}

/**
 * Order a (already filtered) list. `best` is a copy of the server order.
 * `soonest` compares absolute instants, so offsets sort correctly.
 * `nearest` compares `distanceMiles`. An unknown distance (null) sorts last,
 * never as 0 ("right here"). Ties, including two unknown distances, keep the
 * incoming order.
 */
export function sortEvents(
  events: readonly EventSummary[],
  sort: SortMode | undefined,
): EventSummary[] {
  const copy = events.slice();
  if (sort === 'soonest') {
    copy.sort((a, b) => instantMs(a.startInstant) - instantMs(b.startInstant));
  } else if (sort === 'nearest') {
    copy.sort((a, b) => compareDistance(a.distanceMiles, b.distanceMiles));
  }
  return copy;
}

/** Filter, then sort. The input array is left untouched. */
export function applyListView(
  events: readonly EventSummary[],
  filters: ListFilters,
): EventSummary[] {
  return sortEvents(applyFilters(events, filters), filters.sort);
}

/**
 * One sentence for the `aria-live="polite"` results region. Includes the
 * active filter state and the sort so a change is announced even when the
 * result count stays the same.
 */
export function describeResults(count: number, filters: ListFilters): string {
  const phrases = scopePhrases(filters);
  const sort = SORT_PHRASE[filters.sort] ?? 'best match';
  const noun = count === 1 ? 'event' : 'events';
  const lead =
    count === 0
      ? phrases.length > 0
        ? `No events match ${phrases.join(', ')}.`
        : 'No events match.'
      : phrases.length > 0
        ? `${count} ${noun} ${phrases.join(', ')}.`
        : `${count} ${noun}.`;
  return `${lead} Sorted by ${sort}.`;
}

const SORT_PHRASE: Record<SortMode, string> = {
  best: 'best match',
  soonest: 'soonest',
  nearest: 'nearest',
};

function scopePhrases(filters: ListFilters): string[] {
  const phrases: string[] = [];
  if (filters.radiusMiles !== undefined) phrases.push(distancePhrase(filters.radiusMiles));
  if (filters.when === 'weekend') phrases.push('this weekend');
  else if (filters.windowDays !== undefined) phrases.push(windowPhrase(filters.windowDays));
  if (filters.happeningNow) phrases.push('happening now');
  if (filters.offer === 'food') phrases.push('offering food');
  if (filters.offer === 'drink') phrases.push('offering drinks');
  if (filters.confirmedOnly) phrases.push('with confirmed food');
  if (filters.hideRestricted) phrases.push('hiding restricted-audience events');
  return phrases;
}

function distancePhrase(miles: number): string {
  if (miles === 1) return 'within 1 mile';
  if (miles === 5) return 'within 5 miles';
  if (miles === 25) return 'within 25 miles';
  return `within ${miles} miles`;
}

function windowPhrase(days: number): string {
  if (days === 1) return 'today';
  if (days === 3) return 'the next couple of days';
  if (days === 7) return 'this week';
  if (days === 14) return 'the next 2 weeks';
  return `the next ${days} days`;
}

function instantMs(iso: string): number {
  const ms = Date.parse(iso);
  return Number.isNaN(ms) ? Number.POSITIVE_INFINITY : ms;
}

/** Null (unresolved venue) sorts after every known distance. */
function compareDistance(a: number | null, b: number | null): number {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return a - b;
}
